import React, { useState, useEffect } from 'react';
import { Plus, Menu, LayoutList, PanelLeft, Trash2, Edit, Copy, Eye, GripVertical, ChevronDown, ChevronRight, ExternalLink, FileText, Link as LinkIcon, Image, Upload, Hash, Save, Crop } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import Sidebar from '../components/Sidebar';
import api from '../utils/api';
import { toast } from 'sonner';
import MenuEditor from '../components/MenuEditor';
import MenuPreview from '../components/MenuPreview';
import MediaPickerModal from '../components/MediaPickerModal';
import { generateAnchorId } from '../components/BlockEditor';
import TipTapEditor from '../components/TipTapEditor';

const MENU_STYLES = [
  { id: 'horizontal', name: 'Horizontal', icon: LayoutList, description: 'Classic top navigation bar' },
  { id: 'hamburger', name: 'Hamburger', icon: Menu, description: 'Collapsible mobile-style menu' },
  { id: 'sidebar', name: 'Sidebar', icon: PanelLeft, description: 'Vertical sidebar navigation' },
];

const MENU_LOCATIONS = [
  { id: 'header', name: 'Header' },
  { id: 'footer', name: 'Footer' },
  { id: 'sidebar', name: 'Sidebar' },
];

// Logo position options based on menu style
const LOGO_POSITIONS = {
  horizontal: [
    { id: 'left', name: 'Left (before menu items)' },
    { id: 'center', name: 'Center (menu items split)' },
    { id: 'right', name: 'Right (after menu items)' },
  ],
  hamburger: [
    { id: 'left', name: 'Left' },
    { id: 'center', name: 'Center' },
    { id: 'right', name: 'Right' },
  ],
  sidebar: [
    { id: 'top', name: 'Top (above menu items)' },
    { id: 'bottom', name: 'Bottom (below menu items)' },
  ],
};

const NavigationManager = () => {
  const [menus, setMenus] = useState([]);
  const [pages, setPages] = useState([]);
  const [anchors, setAnchors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingMenu, setEditingMenu] = useState(null);
  // Snapshot of the menu as it exists on the server. Used to compute `isDirty`
  // automatically without instrumenting every setEditingMenu call site.
  const [savedMenuJson, setSavedMenuJson] = useState(null);
  const isDirty = !!editingMenu && JSON.stringify(editingMenu) !== savedMenuJson;
  const [siteTheme, setSiteTheme] = useState(null);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [trimmingLogo, setTrimmingLogo] = useState(false);

  const handleTrimLogo = async () => {
    const url = editingMenu?.logo?.url;
    if (!url) return;
    setTrimmingLogo(true);
    try {
      const res = await api.post('/media/trim-whitespace', { url });
      setEditingMenu(prev => ({ ...prev, logo: { ...prev.logo, url: res.data.url } }));
      const [ow, oh] = res.data.original_size || [];
      const [tw, th] = res.data.trimmed_size || [];
      toast.success(`Whitespace trimmed (${ow}×${oh} → ${tw}×${th}). Remember to Save.`);
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Could not trim the logo.';
      toast.error(msg);
    } finally {
      setTrimmingLogo(false);
    }
  };


  // New menu form state
  const [newMenu, setNewMenu] = useState({
    name: '',
    style: 'horizontal',
    location: 'header',
    is_global: true,
    page_ids: [],
    items: [],
    theme_override: null,
    dropdown_trigger: 'click',
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Warn before closing the tab / navigating away if there are unsaved menu edits
  useEffect(() => {
    if (!isDirty) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const fetchData = async () => {
    try {
      const [menusRes, pagesRes, themeRes, landingPageRes] = await Promise.all([
        api.get('/navigation'),
        api.get('/pages'),
        api.get('/theme'),
        api.get('/tenant/landing-page').catch(() => ({ data: null })),
      ]);
      setMenus(menusRes.data);
      setPages(pagesRes.data);
      setSiteTheme(themeRes.data);
      
      let allAnchors = [];
      
      // Extract anchors from landing page HTML sections
      if (landingPageRes.data?.html_sections) {
        const landingAnchors = landingPageRes.data.html_sections
          .filter(section => !section.draft)
          .map(section => ({
            id: section.anchor_id || section.name?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || section.id,
            name: section.name || 'Unnamed Section',
            source: 'Landing Page'
          }));
        allAnchors = [...allAnchors, ...landingAnchors];
      }
      
      // Extract anchors from regular pages
      if (pagesRes.data && pagesRes.data.length > 0) {
        pagesRes.data.forEach(page => {
          if (page.blocks && page.blocks.length > 0) {
            const pageAnchors = page.blocks
              // Include blocks that have a custom anchor_id OR are specific types that auto-generate anchors
              .filter(block => block.content?.anchor_id || ['heading', 'hero', 'form', 'gallery', 'article-feed'].includes(block.type))
              .map(block => ({
                id: block.content?.anchor_id || generateAnchorId(block),
                name: block.content?.anchor_id 
                  ? `#${block.content.anchor_id} (${block.type})`
                  : getBlockDisplayName(block),
                source: page.title || 'Untitled Page',
                pageSlug: page.slug
              }));
            allAnchors = [...allAnchors, ...pageAnchors];
          }
        });
      }
      
      setAnchors(allAnchors);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };
  
  // Helper to get display name for block anchors
  const getBlockDisplayName = (block) => {
    if (block.type === 'heading') {
      return block.content?.html?.replace(/<[^>]*>/g, '').substring(0, 40) || 'Heading';
    } else if (block.type === 'hero') {
      return block.content?.headline?.substring(0, 40) || 'Hero Section';
    } else if (block.type === 'form') {
      return block.content?.formName || 'Form';
    } else if (block.type === 'gallery') {
      return 'Gallery';
    } else if (block.type === 'article-feed') {
      return block.content?.heading || 'Article Feed';
    }
    return block.type;
  };

  const handleCreateMenu = async () => {
    if (!newMenu.name.trim()) {
      toast.error('Please enter a menu name');
      return;
    }

    try {
      const res = await api.post('/navigation', newMenu);
      setMenus([...menus, res.data]);
      setShowCreateModal(false);
      setNewMenu({
        name: '',
        style: 'horizontal',
        location: 'header',
        is_global: true,
        page_ids: [],
        items: [],
        theme_override: null,
        dropdown_trigger: 'click',
      });
      toast.success('Navigation menu created!');
      // Open for editing
      openEditor(res.data);
    } catch (error) {
      console.error('Failed to create menu:', error);
      toast.error('Failed to create menu');
    }
  };

  const handleUpdateMenu = async (menuData) => {
    try {
      const res = await api.put(`/navigation/${menuData.menu_id}`, menuData);
      setMenus(menus.map(m => m.menu_id === menuData.menu_id ? res.data : m));
      setEditingMenu(prev => (prev && prev.menu_id === res.data.menu_id ? res.data : prev));
      // Mark this snapshot as "saved" - clears the dirty flag automatically
      setSavedMenuJson(JSON.stringify(res.data));
      toast.success('Menu saved!');
    } catch (error) {
      console.error('Failed to update menu:', error);
      toast.error('Failed to save menu');
    }
  };

  // Local-only update: mutates editingMenu but does NOT hit the API.
  // The user must click "Save Changes" to persist.
  const updateEditingMenu = (updated) => {
    setEditingMenu(updated);
  };

  // Open a menu for editing: load into editingMenu AND set the saved snapshot
  // so the dirty flag starts as false.
  const openEditor = (menu) => {
    setEditingMenu(menu);
    setSavedMenuJson(JSON.stringify(menu));
  };

  // Wrap "Back to Menus" + menu-switch actions to warn on unsaved edits
  const requestCloseEditor = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard them and leave?')) {
      return;
    }
    setEditingMenu(null);
    setSavedMenuJson(null);
  };

  const handleDeleteMenu = async (menuId) => {
    if (!window.confirm('Are you sure you want to delete this menu?')) return;

    try {
      await api.delete(`/navigation/${menuId}`);
      setMenus(menus.filter(m => m.menu_id !== menuId));
      if (editingMenu?.menu_id === menuId) {
        setEditingMenu(null);
      }
      toast.success('Menu deleted!');
    } catch (error) {
      console.error('Failed to delete menu:', error);
      toast.error('Failed to delete menu');
    }
  };

  const handleDuplicateMenu = async (menu) => {
    try {
      const duplicatedMenu = {
        ...menu,
        name: `${menu.name} (Copy)`,
      };
      delete duplicatedMenu.menu_id;
      delete duplicatedMenu.tenant_id;
      delete duplicatedMenu.created_at;
      delete duplicatedMenu.updated_at;

      const res = await api.post('/navigation', duplicatedMenu);
      setMenus([...menus, res.data]);
      toast.success('Menu duplicated!');
    } catch (error) {
      console.error('Failed to duplicate menu:', error);
      toast.error('Failed to duplicate menu');
    }
  };

  const getStyleIcon = (style) => {
    const styleObj = MENU_STYLES.find(s => s.id === style);
    return styleObj ? styleObj.icon : Menu;
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-slate-50">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 min-w-0 p-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Navigation Menus
            </h1>
            <p className="text-slate-500 mt-1">Create and manage your site navigation</p>
          </div>
          <Button onClick={() => setShowCreateModal(true)} className="gap-2">
            <Plus size={18} />
            Create Menu
          </Button>
        </div>

        {editingMenu ? (
          /* Menu Editor View */
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={requestCloseEditor} data-testid="nav-back-to-menus">
                ← Back to Menus
              </Button>
              <h2 className="text-xl font-semibold">{editingMenu.name}</h2>
              {isDirty && (
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-full" data-testid="nav-unsaved-indicator">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                  Unsaved changes
                </span>
              )}
              <div className="flex-1" />
              <Button
                onClick={() => handleUpdateMenu(editingMenu)}
                disabled={!isDirty}
                className="gap-2"
                data-testid="nav-save-changes-btn"
              >
                <Save size={16} />
                Save Changes
              </Button>
            </div>

            <Tabs defaultValue="items" className="w-full">
              <TabsList>
                <TabsTrigger value="items">Menu Items</TabsTrigger>
                <TabsTrigger value="settings">Settings</TabsTrigger>
                {editingMenu.location === 'footer' && (
                  <TabsTrigger value="footer">Footer Content</TabsTrigger>
                )}
                <TabsTrigger value="style">Style & Theme</TabsTrigger>
                <TabsTrigger value="preview">Preview</TabsTrigger>
              </TabsList>

              <TabsContent value="items" className="mt-6">
                <MenuEditor
                  menu={editingMenu}
                  pages={pages}
                  anchors={anchors}
                  onUpdate={(updatedMenu) => {
                    updateEditingMenu(updatedMenu);
                  }}
                />
              </TabsContent>

              <TabsContent value="settings" className="mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Menu Settings</CardTitle>
                    <CardDescription>Configure where and how your menu appears</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid gap-4">
                      <div className="space-y-2">
                        <Label>Menu Name</Label>
                        <Input
                          value={editingMenu.name}
                          onChange={(e) => updateEditingMenu({ ...editingMenu, name: e.target.value })}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Menu Style</Label>
                        <Select
                          value={editingMenu.style}
                          onValueChange={(value) => {
                            const updated = { ...editingMenu, style: value };
                            updateEditingMenu(updated);
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {MENU_STYLES.map((style) => (
                              <SelectItem key={style.id} value={style.id}>
                                <div className="flex items-center gap-2">
                                  <style.icon size={16} />
                                  <span>{style.name}</span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Hamburger Alignment - Only show when hamburger style is selected */}
                      {editingMenu.style === 'hamburger' && (
                        <div className="space-y-2">
                          <Label>Hamburger Alignment</Label>
                          <p className="text-xs text-slate-500 mb-2">Position of the hamburger button</p>
                          <Select
                            value={editingMenu.hamburger_align || 'right'}
                            onValueChange={(value) => {
                              const updated = { ...editingMenu, hamburger_align: value };
                              updateEditingMenu(updated);
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="left">Left</SelectItem>
                              <SelectItem value="center">Center</SelectItem>
                              <SelectItem value="right">Right</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {/* Horizontal Menu Alignment - Only show when horizontal style is selected */}
                      {editingMenu.style === 'horizontal' && (
                        <div className="space-y-2">
                          <Label>Menu Items Alignment</Label>
                          <p className="text-xs text-slate-500 mb-2">Position of menu items in the navigation bar</p>
                          <Select
                            value={editingMenu.items_align || 'left'}
                            onValueChange={(value) => {
                              const updated = { ...editingMenu, items_align: value };
                              updateEditingMenu(updated);
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="left">Left</SelectItem>
                              <SelectItem value="center">Center</SelectItem>
                              <SelectItem value="right">Right</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {/* Header Content Width + Edge Padding — controls how far in the logo/nav sit */}
                      {editingMenu.location === 'header' && (
                        <>
                          <div className="space-y-2">
                            <Label>Header Content Width</Label>
                            <p className="text-xs text-slate-500 mb-2">How wide the logo/menu area stretches. Use "Full width" to push the logo to the screen edge.</p>
                            <Select
                              value={editingMenu.content_width || 'contained'}
                              onValueChange={(value) => updateEditingMenu({ ...editingMenu, content_width: value })}
                            >
                              <SelectTrigger data-testid="header-content-width-select">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="contained">Contained (1280px, centered)</SelectItem>
                                <SelectItem value="wide">Wide (1536px, centered)</SelectItem>
                                <SelectItem value="full">Full width (edge to edge)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>Edge Padding (px)</Label>
                            <p className="text-xs text-slate-500 mb-2">Space between the logo/menu and the left &amp; right edges. Lower = logo closer to the edge.</p>
                            <Input
                              type="number"
                              min={0}
                              max={200}
                              value={editingMenu.edge_padding ?? 16}
                              onChange={(e) => {
                                const v = parseInt(e.target.value);
                                updateEditingMenu({ ...editingMenu, edge_padding: Number.isNaN(v) ? 0 : v });
                              }}
                              data-testid="header-edge-padding-input"
                            />
                          </div>
                        </>
                      )}


                      <div className="space-y-2">
                        <Label>Location</Label>
                        <Select
                          value={editingMenu.location}
                          onValueChange={(value) => {
                            const updated = { ...editingMenu, location: value };
                            updateEditingMenu(updated);
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {MENU_LOCATIONS.map((loc) => (
                              <SelectItem key={loc.id} value={loc.id}>
                                {loc.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                        <div>
                          <Label>Global Menu</Label>
                          <p className="text-sm text-slate-500">Show this menu on all pages</p>
                        </div>
                        <Switch
                          checked={editingMenu.is_global}
                          onCheckedChange={(checked) => {
                            const updated = { ...editingMenu, is_global: checked };
                            updateEditingMenu(updated);
                          }}
                        />
                      </div>

                      {editingMenu.location === 'header' && (
                        <div className="space-y-2">
                          <Label>Dropdown Trigger</Label>
                          <p className="text-xs text-slate-500 mb-2">How dropdown menus are opened</p>
                          <Select
                            value={editingMenu.dropdown_trigger || 'click'}
                            onValueChange={(value) => {
                              const updated = { ...editingMenu, dropdown_trigger: value };
                              updateEditingMenu(updated);
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="click">Click to open</SelectItem>
                              <SelectItem value="hover">Hover to open</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {editingMenu.location === 'header' && (
                        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                          <div>
                            <Label>Show Search</Label>
                            <p className="text-sm text-slate-500">Display a search icon in the header</p>
                          </div>
                          <Switch
                            checked={editingMenu.show_search === true}
                            onCheckedChange={(checked) => {
                              const updated = { ...editingMenu, show_search: checked };
                              updateEditingMenu(updated);
                            }}
                          />
                        </div>
                      )}

                      {!editingMenu.is_global && (
                        <div className="space-y-2">
                          <Label>Show on Pages</Label>
                          <div className="max-h-48 overflow-y-auto border rounded-lg p-2 space-y-1">
                            {pages.map((page) => (
                              <label key={page.page_id} className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={editingMenu.page_ids?.includes(page.page_id)}
                                  onChange={(e) => {
                                    const newPageIds = e.target.checked
                                      ? [...(editingMenu.page_ids || []), page.page_id]
                                      : (editingMenu.page_ids || []).filter(id => id !== page.page_id);
                                    setEditingMenu({ ...editingMenu, page_ids: newPageIds });
                                  }}
                                  className="rounded"
                                />
                                <FileText size={14} className="text-slate-400" />
                                <span className="text-sm">{page.title || page.slug}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Logo Settings */}
                      <div className="border-t pt-6 mt-6">
                        <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                          <Image size={16} />
                          Logo Settings
                        </h3>
                        
                        <div className="space-y-4">
                          {/* Logo Image */}
                          <div className="space-y-2">
                            <Label>Logo Image</Label>
                            {editingMenu.logo?.url ? (
                              <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                                <img 
                                  src={editingMenu.logo.url} 
                                  alt="Logo" 
                                  style={{ 
                                    height: `${editingMenu.logo?.height || 40}px`,
                                    width: 'auto',
                                    ...(editingMenu.logo?.maxWidth ? { maxWidth: `${editingMenu.logo.maxWidth}px` } : {})
                                  }}
                                  className="object-contain"
                                />
                                <div className="flex-1">
                                  <p className="text-sm text-slate-600 truncate">{editingMenu.logo.url.split('/').pop()}</p>
                                </div>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => setShowMediaPicker(true)}
                                >
                                  Change
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => setEditingMenu({ 
                                    ...editingMenu, 
                                    logo: { ...editingMenu.logo, url: '' } 
                                  })}
                                >
                                  <Trash2 size={14} />
                                </Button>
                              </div>
                            ) : (
                              <Button 
                                variant="outline" 
                                className="w-full justify-center gap-2 h-20 border-dashed"
                                onClick={() => setShowMediaPicker(true)}
                              >
                                <Upload size={18} />
                                <span>Choose Logo from Media Library</span>
                              </Button>
                            )}
                            {editingMenu.logo?.url && (
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={handleTrimLogo}
                                  disabled={trimmingLogo}
                                  data-testid="logo-trim-whitespace-btn"
                                  className="gap-2"
                                >
                                  <Crop size={14} />
                                  {trimmingLogo ? 'Trimming…' : 'Trim whitespace'}
                                </Button>
                                <p className="text-xs text-slate-500">Logo looks small in a tall bar? Trim the blank padding baked into the image.</p>
                              </div>
                            )}
                          </div>

                          {/* Logo Position */}
                          {editingMenu.logo?.url && (
                            <>
                              <div className="space-y-2">
                                <Label>Logo Position</Label>
                                <Select
                                  value={editingMenu.logo?.position || 'left'}
                                  onValueChange={(value) => setEditingMenu({ 
                                    ...editingMenu, 
                                    logo: { ...editingMenu.logo, position: value } 
                                  })}
                                >
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {(LOGO_POSITIONS[editingMenu.style] || LOGO_POSITIONS.horizontal).map((pos) => (
                                      <SelectItem key={pos.id} value={pos.id}>
                                        {pos.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>

                              {/* Logo Size */}
                              <div className="space-y-2">
                                <Label>Logo Height (px)</Label>
                                <Input
                                  type="number"
                                  min={20}
                                  max={140}
                                  value={editingMenu.logo?.height || 40}
                                  onChange={(e) => setEditingMenu({ 
                                    ...editingMenu, 
                                    logo: { ...editingMenu.logo, height: parseInt(e.target.value) || 40 } 
                                  })}
                                  data-testid="logo-height-input"
                                />
                                <p className="text-xs text-slate-500">Recommended: 32-48px for headers. Increase for wide/landscape logos.</p>
                              </div>

                              {/* Logo Max Width */}
                              <div className="space-y-2">
                                <Label>Logo Max Width (px) — optional</Label>
                                <Input
                                  type="number"
                                  min={40}
                                  max={600}
                                  placeholder="Auto (scales with height)"
                                  value={editingMenu.logo?.maxWidth || ''}
                                  onChange={(e) => {
                                    const v = parseInt(e.target.value);
                                    setEditingMenu({ 
                                      ...editingMenu, 
                                      logo: { ...editingMenu.logo, maxWidth: Number.isNaN(v) ? null : v } 
                                    });
                                  }}
                                  data-testid="logo-maxwidth-input"
                                />
                                <p className="text-xs text-slate-500">Leave blank to let the logo scale naturally with the height. Set a cap only if a wide logo gets too big.</p>
                              </div>

                              {/* Logo Link / URL */}
                              <div className="space-y-2">
                                <Label>Logo URL (where it links)</Label>
                                <Input
                                  placeholder="/"
                                  value={editingMenu.logo?.link || ''}
                                  onChange={(e) => setEditingMenu({ 
                                    ...editingMenu, 
                                    logo: { ...editingMenu.logo, link: e.target.value } 
                                  })}
                                  data-testid="nav-logo-link-input"
                                />
                                <p className="text-xs text-slate-500">
                                  Where clicking the logo takes visitors. Leave blank to send them to your homepage (<code className="bg-slate-100 px-1 rounded">/</code>).
                                  You can also enter a full URL like <code className="bg-slate-100 px-1 rounded">https://example.com</code>.
                                </p>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    <Button onClick={() => handleUpdateMenu(editingMenu)}>
                      Save Settings
                    </Button>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="style" className="mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Menu Theme</CardTitle>
                    <CardDescription>Customize the appearance of this menu</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div>
                        <Label>Use Site Theme</Label>
                        <p className="text-sm text-slate-500">Inherit colors from your site theme</p>
                      </div>
                      <Switch
                        checked={!editingMenu.theme_override}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setEditingMenu({ ...editingMenu, theme_override: null });
                          } else {
                            setEditingMenu({
                              ...editingMenu,
                              theme_override: {
                                backgroundColor: siteTheme?.background_color || '#ffffff',
                                textColor: siteTheme?.body_color || '#475569',
                                accentColor: siteTheme?.primary_color || '#6366f1',
                                hoverColor: siteTheme?.link_hover_color || '#4f46e5',
                              },
                            });
                          }
                        }}
                      />
                    </div>

                    {editingMenu.theme_override && (
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Background Color</Label>
                          <div className="flex gap-2">
                            <input
                              type="color"
                              value={editingMenu.theme_override.backgroundColor || '#ffffff'}
                              onChange={(e) => setEditingMenu({
                                ...editingMenu,
                                theme_override: { ...editingMenu.theme_override, backgroundColor: e.target.value }
                              })}
                              className="w-10 h-10 rounded cursor-pointer"
                            />
                            <Input
                              value={editingMenu.theme_override.backgroundColor || '#ffffff'}
                              onChange={(e) => setEditingMenu({
                                ...editingMenu,
                                theme_override: { ...editingMenu.theme_override, backgroundColor: e.target.value }
                              })}
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label>Text Color</Label>
                          <div className="flex gap-2">
                            <input
                              type="color"
                              value={editingMenu.theme_override.textColor || '#475569'}
                              onChange={(e) => setEditingMenu({
                                ...editingMenu,
                                theme_override: { ...editingMenu.theme_override, textColor: e.target.value }
                              })}
                              className="w-10 h-10 rounded cursor-pointer"
                            />
                            <Input
                              value={editingMenu.theme_override.textColor || '#475569'}
                              onChange={(e) => setEditingMenu({
                                ...editingMenu,
                                theme_override: { ...editingMenu.theme_override, textColor: e.target.value }
                              })}
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label>Accent Color</Label>
                          <div className="flex gap-2">
                            <input
                              type="color"
                              value={editingMenu.theme_override.accentColor || '#6366f1'}
                              onChange={(e) => setEditingMenu({
                                ...editingMenu,
                                theme_override: { ...editingMenu.theme_override, accentColor: e.target.value }
                              })}
                              className="w-10 h-10 rounded cursor-pointer"
                            />
                            <Input
                              value={editingMenu.theme_override.accentColor || '#6366f1'}
                              onChange={(e) => setEditingMenu({
                                ...editingMenu,
                                theme_override: { ...editingMenu.theme_override, accentColor: e.target.value }
                              })}
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label>Hover Color</Label>
                          <div className="flex gap-2">
                            <input
                              type="color"
                              value={editingMenu.theme_override.hoverColor || '#4f46e5'}
                              onChange={(e) => setEditingMenu({
                                ...editingMenu,
                                theme_override: { ...editingMenu.theme_override, hoverColor: e.target.value }
                              })}
                              className="w-10 h-10 rounded cursor-pointer"
                            />
                            <Input
                              value={editingMenu.theme_override.hoverColor || '#4f46e5'}
                              onChange={(e) => setEditingMenu({
                                ...editingMenu,
                                theme_override: { ...editingMenu.theme_override, hoverColor: e.target.value }
                              })}
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    <Button onClick={() => handleUpdateMenu(editingMenu)}>
                      Save Theme
                    </Button>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Footer Content Tab - Only for footer menus */}
              {editingMenu.location === 'footer' && (
                <TabsContent value="footer" className="mt-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Footer Content</CardTitle>
                      <CardDescription>Add custom content sections to your footer</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* Footer Layout */}
                      <div className="space-y-2">
                        <Label>Footer Layout</Label>
                        <Select
                          value={editingMenu.footer_settings?.layout || 'simple'}
                          onValueChange={(value) => setEditingMenu({
                            ...editingMenu,
                            footer_settings: { ...editingMenu.footer_settings, layout: value }
                          })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="simple">Simple (Links centered)</SelectItem>
                            <SelectItem value="columns">Columns (Links + Content sections)</SelectItem>
                            <SelectItem value="stacked">Stacked (Content above links)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Content Alignment */}
                      <div className="space-y-2">
                        <Label>Content Alignment</Label>
                        <Select
                          value={editingMenu.footer_settings?.content_align || (editingMenu.footer_settings?.layout === 'columns' ? 'left' : 'center')}
                          onValueChange={(value) => setEditingMenu({
                            ...editingMenu,
                            footer_settings: { ...editingMenu.footer_settings, content_align: value }
                          })}
                        >
                          <SelectTrigger data-testid="footer-content-align-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="left">Left (headers track their content)</SelectItem>
                            <SelectItem value="center">Center</SelectItem>
                            <SelectItem value="right">Right</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-slate-400">Aligns each section's header, content and links together. Use the "Columns" layout with "Left" so headings like "Our Address" and "Quick Links" sit above their own columns.</p>
                      </div>

                      {/* Content Sections */}
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <Label>Content Sections</Label>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const sections = editingMenu.footer_settings?.sections || [];
                              setEditingMenu({
                                ...editingMenu,
                                footer_settings: {
                                  ...editingMenu.footer_settings,
                                  sections: [...sections, {
                                    section_id: `section-${Date.now()}`,
                                    title: 'New Section',
                                    content_html: '<p>Add your content here...</p>'
                                  }]
                                }
                              });
                            }}
                          >
                            <Plus size={16} className="mr-1" />
                            Add Section
                          </Button>
                        </div>

                        {/* Section Title Colour — applies to all content-section titles */}
                        <div className="space-y-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
                          <div className="flex items-center justify-between">
                            <Label className="text-sm">Section Title Colour</Label>
                            {editingMenu.footer_settings?.section_title_color && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 text-xs text-slate-500"
                                data-testid="footer-section-title-color-reset"
                                onClick={() => {
                                  const fs = { ...editingMenu.footer_settings };
                                  delete fs.section_title_color;
                                  setEditingMenu({ ...editingMenu, footer_settings: fs });
                                }}
                              >
                                Reset
                              </Button>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <input
                              type="color"
                              data-testid="footer-section-title-color-picker"
                              value={editingMenu.footer_settings?.section_title_color || '#475569'}
                              onChange={(e) => setEditingMenu({
                                ...editingMenu,
                                footer_settings: { ...editingMenu.footer_settings, section_title_color: e.target.value }
                              })}
                              className="w-10 h-10 rounded cursor-pointer"
                            />
                            <Input
                              data-testid="footer-section-title-color-input"
                              placeholder="Inherits footer text colour"
                              value={editingMenu.footer_settings?.section_title_color || ''}
                              onChange={(e) => setEditingMenu({
                                ...editingMenu,
                                footer_settings: { ...editingMenu.footer_settings, section_title_color: e.target.value }
                              })}
                            />
                          </div>
                          <p className="text-xs text-slate-400">Applies to every content-section title. Leave blank to inherit the footer text colour.</p>
                        </div>

                        {/* Footer Link Colour — applies to all footer link text (menu items + section links) */}
                        <div className="space-y-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
                          <div className="flex items-center justify-between">
                            <Label className="text-sm">Footer Link Colour</Label>
                            {editingMenu.footer_settings?.link_color && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 text-xs text-slate-500"
                                data-testid="footer-link-color-reset"
                                onClick={() => {
                                  const fs = { ...editingMenu.footer_settings };
                                  delete fs.link_color;
                                  setEditingMenu({ ...editingMenu, footer_settings: fs });
                                }}
                              >
                                Reset
                              </Button>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <input
                              type="color"
                              data-testid="footer-link-color-picker"
                              value={editingMenu.footer_settings?.link_color || '#475569'}
                              onChange={(e) => setEditingMenu({
                                ...editingMenu,
                                footer_settings: { ...editingMenu.footer_settings, link_color: e.target.value }
                              })}
                              className="w-10 h-10 rounded cursor-pointer"
                            />
                            <Input
                              data-testid="footer-link-color-input"
                              placeholder="Inherits footer text colour"
                              value={editingMenu.footer_settings?.link_color || ''}
                              onChange={(e) => setEditingMenu({
                                ...editingMenu,
                                footer_settings: { ...editingMenu.footer_settings, link_color: e.target.value }
                              })}
                            />
                          </div>
                          <p className="text-xs text-slate-400">Applies to all footer links (Quick Links and section links). Leave blank to inherit the footer text colour.</p>
                        </div>

                        {(editingMenu.footer_settings?.sections || []).length === 0 ? (
                          <div className="p-6 bg-slate-50 border-2 border-dashed border-slate-200 rounded-lg text-center">
                            <p className="text-slate-500 text-sm">No content sections yet</p>
                            <p className="text-slate-400 text-xs mt-1">Add sections for contact info, hours, about text, etc.</p>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {(editingMenu.footer_settings?.sections || []).map((section, index) => (
                              <div key={section.section_id} className="p-4 border border-slate-200 rounded-lg bg-white">
                                <div className="flex items-start justify-between mb-3">
                                  <Input
                                    value={section.title || ''}
                                    onChange={(e) => {
                                      const sections = [...(editingMenu.footer_settings?.sections || [])];
                                      sections[index] = { ...sections[index], title: e.target.value };
                                      setEditingMenu({
                                        ...editingMenu,
                                        footer_settings: { ...editingMenu.footer_settings, sections }
                                      });
                                    }}
                                    placeholder="Section Title (e.g., Contact Us)"
                                    className="font-medium"
                                  />
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      const sections = (editingMenu.footer_settings?.sections || []).filter((_, i) => i !== index);
                                      setEditingMenu({
                                        ...editingMenu,
                                        footer_settings: { ...editingMenu.footer_settings, sections }
                                      });
                                    }}
                                    className="text-red-500 hover:text-red-700 ml-2"
                                  >
                                    <Trash2 size={16} />
                                  </Button>
                                </div>
                                
                                {/* HTML Content (WYSIWYG) */}
                                <Label className="text-xs text-slate-600 mb-1 block">Content</Label>
                                <div className="mb-4">
                                  <TipTapEditor
                                    content={section.content_html || ''}
                                    onChange={(html) => {
                                      const sections = [...(editingMenu.footer_settings?.sections || [])];
                                      // Avoid setting on every keystroke if nothing changed
                                      if (sections[index]?.content_html === html) return;
                                      sections[index] = { ...sections[index], content_html: html };
                                      setEditingMenu({
                                        ...editingMenu,
                                        footer_settings: { ...editingMenu.footer_settings, sections }
                                      });
                                    }}
                                    placeholder="Add formatted content (contact info, hours, about text, etc.)"
                                  />
                                </div>
                                
                                {/* Section Links */}
                                <div className="border-t border-slate-100 pt-3 mt-3">
                                  <div className="flex items-center justify-between mb-2">
                                    <Label className="text-xs text-slate-600 flex items-center gap-1">
                                      <LinkIcon size={12} />
                                      Links
                                    </Label>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 text-xs"
                                      onClick={() => {
                                        const sections = [...(editingMenu.footer_settings?.sections || [])];
                                        const sectionLinks = sections[index].links || [];
                                        sections[index] = {
                                          ...sections[index],
                                          links: [...sectionLinks, {
                                            link_id: `link-${Date.now()}`,
                                            label: 'New Link',
                                            link_type: 'external',
                                            url: ''
                                          }]
                                        };
                                        setEditingMenu({
                                          ...editingMenu,
                                          footer_settings: { ...editingMenu.footer_settings, sections }
                                        });
                                      }}
                                    >
                                      <Plus size={14} className="mr-1" />
                                      Add Link
                                    </Button>
                                  </div>
                                  
                                  {(section.links || []).length === 0 ? (
                                    <p className="text-xs text-slate-400 italic">No links added yet</p>
                                  ) : (
                                    <div className="space-y-2">
                                      {(section.links || []).map((link, linkIndex) => (
                                        <div key={link.link_id} className="flex items-center gap-2 p-2 bg-slate-50 rounded-md">
                                          <div className="flex-1 grid grid-cols-3 gap-2">
                                            <Input
                                              value={link.label || ''}
                                              onChange={(e) => {
                                                const sections = [...(editingMenu.footer_settings?.sections || [])];
                                                const links = [...(sections[index].links || [])];
                                                links[linkIndex] = { ...links[linkIndex], label: e.target.value };
                                                sections[index] = { ...sections[index], links };
                                                setEditingMenu({
                                                  ...editingMenu,
                                                  footer_settings: { ...editingMenu.footer_settings, sections }
                                                });
                                              }}
                                              placeholder="Link Label"
                                              className="h-8 text-sm"
                                            />
                                            <Select
                                              value={link.link_type || 'external'}
                                              onValueChange={(value) => {
                                                const sections = [...(editingMenu.footer_settings?.sections || [])];
                                                const links = [...(sections[index].links || [])];
                                                links[linkIndex] = { ...links[linkIndex], link_type: value, url: '', page_id: '' };
                                                sections[index] = { ...sections[index], links };
                                                setEditingMenu({
                                                  ...editingMenu,
                                                  footer_settings: { ...editingMenu.footer_settings, sections }
                                                });
                                              }}
                                            >
                                              <SelectTrigger className="h-8 text-sm">
                                                <SelectValue />
                                              </SelectTrigger>
                                              <SelectContent>
                                                <SelectItem value="external">
                                                  <span className="flex items-center gap-1">
                                                    <ExternalLink size={12} />
                                                    External URL
                                                  </span>
                                                </SelectItem>
                                                <SelectItem value="internal">
                                                  <span className="flex items-center gap-1">
                                                    <FileText size={12} />
                                                    Internal Page
                                                  </span>
                                                </SelectItem>
                                              </SelectContent>
                                            </Select>
                                            
                                            {link.link_type === 'internal' ? (
                                              <Select
                                                value={link.page_id || ''}
                                                onValueChange={(value) => {
                                                  const sections = [...(editingMenu.footer_settings?.sections || [])];
                                                  const links = [...(sections[index].links || [])];
                                                  const selectedPage = pages.find(p => p.page_id === value);
                                                  links[linkIndex] = { 
                                                    ...links[linkIndex], 
                                                    page_id: value,
                                                    url: selectedPage ? `/${selectedPage.slug}` : ''
                                                  };
                                                  sections[index] = { ...sections[index], links };
                                                  setEditingMenu({
                                                    ...editingMenu,
                                                    footer_settings: { ...editingMenu.footer_settings, sections }
                                                  });
                                                }}
                                              >
                                                <SelectTrigger className="h-8 text-sm">
                                                  <SelectValue placeholder="Select page..." />
                                                </SelectTrigger>
                                                <SelectContent>
                                                  {pages.map((page) => (
                                                    <SelectItem key={page.page_id} value={page.page_id}>
                                                      {page.title || page.slug || 'Untitled'}
                                                    </SelectItem>
                                                  ))}
                                                </SelectContent>
                                              </Select>
                                            ) : (
                                              <Input
                                                value={link.url || ''}
                                                onChange={(e) => {
                                                  const sections = [...(editingMenu.footer_settings?.sections || [])];
                                                  const links = [...(sections[index].links || [])];
                                                  links[linkIndex] = { ...links[linkIndex], url: e.target.value };
                                                  sections[index] = { ...sections[index], links };
                                                  setEditingMenu({
                                                    ...editingMenu,
                                                    footer_settings: { ...editingMenu.footer_settings, sections }
                                                  });
                                                }}
                                                placeholder="https://..."
                                                className="h-8 text-sm"
                                              />
                                            )}
                                          </div>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                                            onClick={() => {
                                              const sections = [...(editingMenu.footer_settings?.sections || [])];
                                              const links = (sections[index].links || []).filter((_, i) => i !== linkIndex);
                                              sections[index] = { ...sections[index], links };
                                              setEditingMenu({
                                                ...editingMenu,
                                                footer_settings: { ...editingMenu.footer_settings, sections }
                                              });
                                            }}
                                          >
                                            <Trash2 size={14} />
                                          </Button>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Copyright Settings */}
                      <div className="pt-4 border-t border-slate-200 space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <Label>Show Copyright</Label>
                            <p className="text-xs text-slate-500">Display copyright text at bottom</p>
                          </div>
                          <Switch
                            checked={editingMenu.footer_settings?.show_copyright !== false}
                            onCheckedChange={(checked) => setEditingMenu({
                              ...editingMenu,
                              footer_settings: { ...editingMenu.footer_settings, show_copyright: checked }
                            })}
                          />
                        </div>
                        
                        {editingMenu.footer_settings?.show_copyright !== false && (
                          <>
                            <div className="space-y-2">
                              <Label>Custom Copyright Text</Label>
                              <Input
                                placeholder={`© ${new Date().getFullYear()} Your Company. All rights reserved.`}
                                value={editingMenu.footer_settings?.copyright_text || ''}
                                onChange={(e) => setEditingMenu({
                                  ...editingMenu,
                                  footer_settings: { ...editingMenu.footer_settings, copyright_text: e.target.value }
                                })}
                              />
                              <p className="text-xs text-slate-400">Leave blank for default copyright text</p>
                            </div>

                            {/* Copyright Text Colour */}
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <Label>Copyright Text Colour</Label>
                                {editingMenu.footer_settings?.copyright_color && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 text-xs text-slate-500"
                                    data-testid="footer-copyright-color-reset"
                                    onClick={() => {
                                      const fs = { ...editingMenu.footer_settings };
                                      delete fs.copyright_color;
                                      setEditingMenu({ ...editingMenu, footer_settings: fs });
                                    }}
                                  >
                                    Reset
                                  </Button>
                                )}
                              </div>
                              <div className="flex gap-2">
                                <input
                                  type="color"
                                  data-testid="footer-copyright-color-picker"
                                  value={editingMenu.footer_settings?.copyright_color || '#475569'}
                                  onChange={(e) => setEditingMenu({
                                    ...editingMenu,
                                    footer_settings: { ...editingMenu.footer_settings, copyright_color: e.target.value }
                                  })}
                                  className="w-10 h-10 rounded cursor-pointer"
                                />
                                <Input
                                  data-testid="footer-copyright-color-input"
                                  placeholder="Inherits footer text colour"
                                  value={editingMenu.footer_settings?.copyright_color || ''}
                                  onChange={(e) => setEditingMenu({
                                    ...editingMenu,
                                    footer_settings: { ...editingMenu.footer_settings, copyright_color: e.target.value }
                                  })}
                                />
                              </div>
                              <p className="text-xs text-slate-400">Leave blank to inherit the footer text colour.</p>
                            </div>
                          </>
                        )}
                      </div>

                      {/* Social Links */}
                      <div className="pt-4 border-t border-slate-200 space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <Label>Social Links</Label>
                            <p className="text-xs text-slate-500">Add social media icons to footer</p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const socialLinks = editingMenu.footer_settings?.social_links || [];
                              setEditingMenu({
                                ...editingMenu,
                                footer_settings: {
                                  ...editingMenu.footer_settings,
                                  social_links: [...socialLinks, {
                                    id: `social-${Date.now()}`,
                                    platform: 'facebook',
                                    url: ''
                                  }]
                                }
                              });
                            }}
                          >
                            <Plus size={16} className="mr-1" />
                            Add Social Link
                          </Button>
                        </div>
                        
                        {(editingMenu.footer_settings?.social_links || []).length > 0 && (
                          <div className="space-y-3">
                            {(editingMenu.footer_settings?.social_links || []).map((link, index) => (
                              <div key={link.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                                <Select
                                  value={link.platform}
                                  onValueChange={(value) => {
                                    const socialLinks = [...(editingMenu.footer_settings?.social_links || [])];
                                    socialLinks[index] = { ...socialLinks[index], platform: value };
                                    setEditingMenu({
                                      ...editingMenu,
                                      footer_settings: { ...editingMenu.footer_settings, social_links: socialLinks }
                                    });
                                  }}
                                >
                                  <SelectTrigger className="w-36">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="facebook">Facebook</SelectItem>
                                    <SelectItem value="twitter">Twitter/X</SelectItem>
                                    <SelectItem value="instagram">Instagram</SelectItem>
                                    <SelectItem value="linkedin">LinkedIn</SelectItem>
                                    <SelectItem value="youtube">YouTube</SelectItem>
                                    <SelectItem value="bluesky">Bluesky</SelectItem>
                                    <SelectItem value="tiktok">TikTok</SelectItem>
                                    <SelectItem value="pinterest">Pinterest</SelectItem>
                                    <SelectItem value="github">GitHub</SelectItem>
                                    <SelectItem value="email">Email</SelectItem>
                                    <SelectItem value="website">Website</SelectItem>
                                  </SelectContent>
                                </Select>
                                <Input
                                  placeholder="https://..."
                                  value={link.url || ''}
                                  className="flex-1"
                                  onChange={(e) => {
                                    const socialLinks = [...(editingMenu.footer_settings?.social_links || [])];
                                    socialLinks[index] = { ...socialLinks[index], url: e.target.value };
                                    setEditingMenu({
                                      ...editingMenu,
                                      footer_settings: { ...editingMenu.footer_settings, social_links: socialLinks }
                                    });
                                  }}
                                />
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-500 hover:text-red-700"
                                  onClick={() => {
                                    const socialLinks = (editingMenu.footer_settings?.social_links || [])
                                      .filter((_, i) => i !== index);
                                    setEditingMenu({
                                      ...editingMenu,
                                      footer_settings: { ...editingMenu.footer_settings, social_links: socialLinks }
                                    });
                                  }}
                                >
                                  <Trash2 size={16} />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Spacing Settings */}
                      <div className="pt-4 border-t border-slate-200 grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label>Space Above (px)</Label>
                          <Input
                            type="number"
                            min={0}
                            max={200}
                            value={editingMenu.footer_settings?.margin_top ?? 48}
                            onChange={(e) => setEditingMenu({
                              ...editingMenu,
                              footer_settings: { ...editingMenu.footer_settings, margin_top: parseInt(e.target.value) || 0 }
                            })}
                            data-testid="footer-margin-top-input"
                          />
                          <p className="text-[11px] text-slate-500">Set to 0 to make the last block butt against the footer.</p>
                        </div>
                        <div className="space-y-2">
                          <Label>Top Padding (px)</Label>
                          <Input
                            type="number"
                            min={0}
                            max={200}
                            value={editingMenu.footer_settings?.padding_top || 48}
                            onChange={(e) => setEditingMenu({
                              ...editingMenu,
                              footer_settings: { ...editingMenu.footer_settings, padding_top: parseInt(e.target.value) || 48 }
                            })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Bottom Padding (px)</Label>
                          <Input
                            type="number"
                            min={0}
                            max={200}
                            value={editingMenu.footer_settings?.padding_bottom || 32}
                            onChange={(e) => setEditingMenu({
                              ...editingMenu,
                              footer_settings: { ...editingMenu.footer_settings, padding_bottom: parseInt(e.target.value) || 32 }
                            })}
                          />
                        </div>
                      </div>

                      <Button onClick={() => handleUpdateMenu(editingMenu)}>
                        Save Footer Settings
                      </Button>
                    </CardContent>
                  </Card>
                </TabsContent>
              )}

              <TabsContent value="preview" className="mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Menu Preview</CardTitle>
                    <CardDescription>See how your menu will look</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <MenuPreview menu={editingMenu} siteTheme={siteTheme} pages={pages} />
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        ) : (
          /* Menu List View */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {menus.length === 0 ? (
              <Card className="col-span-full">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Menu size={48} className="text-slate-300 mb-4" />
                  <h3 className="text-lg font-semibold text-slate-700">No menus yet</h3>
                  <p className="text-slate-500 text-center mt-1 mb-4">
                    Create your first navigation menu to get started
                  </p>
                  <Button onClick={() => setShowCreateModal(true)} className="gap-2">
                    <Plus size={18} />
                    Create Menu
                  </Button>
                </CardContent>
              </Card>
            ) : (
              menus.map((menu) => {
                const StyleIcon = getStyleIcon(menu.style);
                return (
                  <Card key={menu.menu_id} className="hover:shadow-md transition-shadow">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-indigo-100 rounded-lg">
                            <StyleIcon size={20} className="text-indigo-600" />
                          </div>
                          <div>
                            <CardTitle className="text-lg">{menu.name}</CardTitle>
                            <CardDescription className="text-xs mt-0.5">
                              {menu.style} • {menu.location} • {menu.items?.length || 0} items
                            </CardDescription>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-2 mb-3">
                        {menu.is_global ? (
                          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                            Global
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                            {menu.page_ids?.length || 0} pages
                          </span>
                        )}
                        <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full capitalize">
                          {menu.location}
                        </span>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => openEditor(menu)}
                        >
                          <Edit size={14} className="mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDuplicateMenu(menu)}
                        >
                          <Copy size={14} />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteMenu(menu.menu_id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        )}

        {/* Create Menu Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Create Navigation Menu</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Menu Name *</Label>
                <Input
                  placeholder="e.g., Main Navigation"
                  value={newMenu.name}
                  onChange={(e) => setNewMenu({ ...newMenu, name: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label>Menu Style</Label>
                <div className="grid grid-cols-3 gap-2">
                  {MENU_STYLES.map((style) => (
                    <button
                      key={style.id}
                      onClick={() => setNewMenu({ ...newMenu, style: style.id })}
                      className={`p-3 rounded-lg border-2 transition-all text-center ${
                        newMenu.style === style.id
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <style.icon size={24} className={`mx-auto mb-1 ${
                        newMenu.style === style.id ? 'text-indigo-600' : 'text-slate-400'
                      }`} />
                      <span className="text-xs font-medium">{style.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Location</Label>
                <Select
                  value={newMenu.location}
                  onValueChange={(value) => setNewMenu({ ...newMenu, location: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MENU_LOCATIONS.map((loc) => (
                      <SelectItem key={loc.id} value={loc.id}>
                        {loc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <Label className="text-sm">Global Menu</Label>
                  <p className="text-xs text-slate-500">Show on all pages</p>
                </div>
                <Switch
                  checked={newMenu.is_global}
                  onCheckedChange={(checked) => setNewMenu({ ...newMenu, is_global: checked })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateMenu}>
                Create Menu
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Media Picker for Logo */}
        <MediaPickerModal
          open={showMediaPicker}
          onClose={() => setShowMediaPicker(false)}
          onSelect={(media) => {
            if (editingMenu) {
              setEditingMenu({
                ...editingMenu,
                logo: {
                  ...editingMenu.logo,
                  url: media.url,
                  alt: media.alt || 'Logo',
                }
              });
            }
            setShowMediaPicker(false);
          }}
        />
      </div>
    </div>
  );
};

export default NavigationManager;
